import { type Hex, getAddress } from 'viem'
import z from 'zod'
import { MAX_UINT256 } from '../constants'
import { granularityOptions } from '../constants/granularity'
import { intervalOptions } from '../constants/interval'
import { NETWORKS, type Network } from '../types/Network'
import { getCaipFromNetwork } from '../utils/getCaipFromNetwork'
import { getNetworkNameFromNetworkOrCaip } from '../utils/getNetworkNameFromNetworkOrCaip'
import { fromVaultId, toVaultId } from '../utils/vaultId'

export const validationErrorMessages = {
  INVALID_ADDRESS: 'Value is not a valid address',
  INVALID_HEX: 'Value is not a valid hex string',
  INVALID_NETWORK: 'Invalid Network param',
  INVALID_VAULT_ID: 'Value is not a valid vault id',
  INVALID_INTERVAL: `Interval invalid only valid options are: ${intervalOptions.join(', ')}`,
  INVALID_GRANULARITY: `Granularity invalid only valid options are: ${granularityOptions.join(', ')}`,
  INVALID_TIMESTAMP: 'Timestamp must be a number greater than 0 and less than 1e11',
}

export const addressSchema = z
  .string()
  .regex(/^0x[a-fA-F0-9]{40}$/, validationErrorMessages.INVALID_ADDRESS)
  .transform((address) => getAddress(address))

export const vaultIdSchema = z.string().transform((vaultId, ctx) => {
  try {
    const { address, uniqueDescriptor } = fromVaultId(vaultId)
    return toVaultId(address, uniqueDescriptor)
  } catch {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: validationErrorMessages.INVALID_VAULT_ID,
    })
    return z.NEVER
  }
})

export const hexSchema = z
  .string()
  .regex(/^0x[a-fA-F0-9]*$/, validationErrorMessages.INVALID_HEX)
  .transform((hex) => hex as Hex)

export const booleanSchema = z.preprocess((value, ctx) => {
  const result = z.enum(['true', 'false']).safeParse(value)
  if (!result.success) {
    for (const issue of result.error.issues) ctx.addIssue(issue)
    // The issue already marks parsing as failed; avoid a second boolean type error.
    return false
  }
  return result.data === 'true'
}, z.boolean())

export const nonNullStringSchema = z.string().min(1)
export const stringArraySchema = z.array(nonNullStringSchema)

export const numberSchema = z.number({ coerce: true })
export const intSchema = numberSchema.int()

export const intervalSchema = z.enum(intervalOptions, {
  errorMap: () => ({ message: validationErrorMessages.INVALID_INTERVAL }),
})

export const granularitySchema = z.enum(granularityOptions, {
  errorMap: () => ({ message: validationErrorMessages.INVALID_GRANULARITY }),
})

export const networkNameSchema = z.enum(NETWORKS, {
  errorMap: () => ({ message: validationErrorMessages.INVALID_NETWORK }),
})

const caipNetworks = NETWORKS.map((network) => getCaipFromNetwork(network))
export const networkOrCaipOptions = [...NETWORKS, ...caipNetworks] as const
export const networkOrCaipSchema = z.enum(networkOrCaipOptions, {
  errorMap: () => ({ message: validationErrorMessages.INVALID_NETWORK }),
})
export type NetworkOrCaip = z.infer<typeof networkOrCaipSchema>

export const networkOrCaipInputSchema = networkOrCaipSchema.transform(
  (network): Network => getNetworkNameFromNetworkOrCaip(network)
)

export const networksArraySchema = z.array(networkOrCaipSchema)
export type NetworksArray = z.infer<typeof networksArraySchema>

export const networkCaipSchema = z.string().regex(/^eip155:\d+$/)
export const assetCaipSchema = z.string().regex(/^eip155:\d+\/(erc20:0x[0-9a-fA-F]{40}|slip44:\d+)$/)

export const positiveBigintSchema = z
  .string()
  .superRefine((arg, ctx) => {
    try {
      const bigintArg = BigInt(arg)
      if (bigintArg > MAX_UINT256) {
        ctx.addIssue({ code: z.ZodIssueCode.too_big, maximum: MAX_UINT256, type: 'bigint', inclusive: true })
      }
      if (bigintArg <= 0n) {
        ctx.addIssue({
          code: z.ZodIssueCode.too_small,
          minimum: 0n,
          type: 'bigint',
          inclusive: false,
          message: 'BigInt must be greater than 0',
        })
      }
    } catch {
      ctx.addIssue({ code: z.ZodIssueCode.invalid_type, expected: 'bigint', received: typeof arg })
    }
  })
  .transform((arg) => BigInt(arg))
export const bigintSchema = z
  .string()
  .superRefine((arg, ctx) => {
    try {
      const bigintArg = BigInt(arg)
      if (bigintArg > MAX_UINT256) {
        ctx.addIssue({ code: z.ZodIssueCode.too_big, maximum: MAX_UINT256, type: 'bigint', inclusive: true })
      }
      if (bigintArg < 0n) {
        ctx.addIssue({
          code: z.ZodIssueCode.too_small,
          minimum: 0n,
          type: 'bigint',
          inclusive: true,
          message: 'BigInt must be greater than or equal to 0',
        })
      }
    } catch {
      ctx.addIssue({ code: z.ZodIssueCode.invalid_type, expected: 'bigint', received: typeof arg })
    }
  })
  .transform((arg) => BigInt(arg))

export const timestampSchema = z.coerce
  .number({ message: validationErrorMessages.INVALID_TIMESTAMP })
  .int()
  .refine((val) => val >= 0 && val < 1e11, {
    message: validationErrorMessages.INVALID_TIMESTAMP,
  })

export const apyBreakdownResponseSchema = z.object({
  base: z.number().describe('Base APY'),
  reward: z.number().describe('Reward APY'),
  total: z.number().describe('Total APY'),
})
export type ApyBreakdown = z.infer<typeof apyBreakdownResponseSchema>

export const apyIntervalBreakdownResponseSchema = z.object({
  '1day': apyBreakdownResponseSchema.describe('1day APY details'),
  '7day': apyBreakdownResponseSchema.describe('7day APY details'),
  '30day': apyBreakdownResponseSchema.describe('30day APY details'),
})
export type ApyIntervalBreakdown = z.infer<typeof apyIntervalBreakdownResponseSchema>

export const sortOrderSchema = z
  .enum(['asc', 'desc'])
  .optional()
  .default('asc')
  .describe('Sort order, either asc or desc. Ascending is default.')
export type SortOrder = z.infer<typeof sortOrderSchema>
